import React, { useState, useMemo, useEffect } from 'react';
import { Modal } from '@components/molecules/Modal';
import { Input } from '@components/atoms/Input';
import { TaskTemplateCard } from '@components/molecules/TaskTemplateCard';
import { TaskTemplate, TaskTemplateData } from '@models/taskTemplate.model';
import { useTaskTemplates } from '@hooks/api/taskTemplates/useTaskTemplates';
import { Search, FileText, ChevronLeft } from 'lucide-react';
import { formatForDisplay, parseDateOnly } from '@utils/formatDate';
import { addWorkingDays, nextWorkingDay } from '@utils/workingDays';

interface SelectTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: TaskTemplate, adjustEndDates: boolean) => void;
  projectStartDate?: string;
  projectEndDate?: string;
  title?: string;
  confirmText?: string;
}

const calculateLastTaskEndDate = (
  importStartDate: string,
  tasks: TaskTemplateData[]
): Date | null => {
  if (!importStartDate || !tasks.length) return null;

  let maxDays = 0;
  tasks.forEach((task) => {
    const taskEnd = (task.daysFromStart || 0) + (task.duration || 0);
    if (taskEnd > maxDays) maxDays = taskEnd;
  });

  // Anchored on a working day, exactly as the server does when it rebuilds the dates.
  return addWorkingDays(nextWorkingDay(parseDateOnly(importStartDate)), maxDays);
};

type Step = 'select' | 'configure';

export const SelectTemplateModal: React.FC<SelectTemplateModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  projectStartDate,
  projectEndDate,
  title = 'Select Template',
  confirmText = 'Use Template',
}) => {
  const { data: templates = [], isLoading } = useTaskTemplates();
  const [step, setStep] = useState<Step>('select');
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [adjustEndDates, setAdjustEndDates] = useState(false);
  const [exceedsProjectRange, setExceedsProjectRange] = useState(false);
  const [lastTaskEndDate, setLastTaskEndDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedTemplate(null);
      setSearchQuery('');
      setStep('select');
      setAdjustEndDates(false);
      setExceedsProjectRange(false);
      setLastTaskEndDate(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedTemplate && projectStartDate && projectEndDate) {
      const calculatedEnd = calculateLastTaskEndDate(
        projectStartDate,
        selectedTemplate.tasks
      );
      setLastTaskEndDate(calculatedEnd);

      if (calculatedEnd) {
        const projectEnd = parseDateOnly(projectEndDate);
        setExceedsProjectRange(calculatedEnd > projectEnd);
      }
    } else {
      setExceedsProjectRange(false);
      setLastTaskEndDate(null);
    }
  }, [selectedTemplate, projectStartDate, projectEndDate]);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery) return templates;

    const lowerQuery = searchQuery.toLowerCase();
    return templates.filter(
      (template) =>
        template.name.toLowerCase().includes(lowerQuery) ||
        template.description?.toLowerCase().includes(lowerQuery)
    );
  }, [templates, searchQuery]);

  const handleConfirm = () => {
    if (step === 'select' && selectedTemplate) {
      // If dates might exceed, go to configure step
      if (exceedsProjectRange) {
        setStep('configure');
      } else {
        // No date issues, proceed directly
        onSelect(selectedTemplate, false);
        onClose();
      }
    } else if (step === 'configure' && selectedTemplate) {
      onSelect(selectedTemplate, adjustEndDates);
      onClose();
    }
  };

  const handleBack = () => {
    setStep('select');
    setAdjustEndDates(false);
  };

  const handleTemplateClick = (template: TaskTemplate) => {
    setSelectedTemplate(template);
  };

  const getTitle = () => {
    switch (step) {
      case 'select':
        return title;
      case 'configure':
        return 'Configure Import';
      default:
        return title;
    }
  };

  const getConfirmText = () => {
    switch (step) {
      case 'select':
        return exceedsProjectRange ? 'Next' : confirmText;
      case 'configure':
        return 'Create with Template';
      default:
        return confirmText;
    }
  };

  const renderSelectStep = () => (
    <div className="flex flex-col gap-4 h-full">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search templates..."
          className="pl-10"
        />
      </div>

      {selectedTemplate && (
        <div className="text-sm text-indigo-600 font-medium">
          Selected: {selectedTemplate.name}
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-[300px]">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">Loading templates...</div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <FileText className="w-12 h-12 mb-2 text-gray-300" />
            {searchQuery ? (
              <p>No templates match your search</p>
            ) : (
              <p>No templates available</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filteredTemplates.map((template) => (
              <TaskTemplateCard
                key={template.templateId}
                template={template}
                isSelected={selectedTemplate?.templateId === template.templateId}
                onClick={() => handleTemplateClick(template)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderConfigureStep = () => (
    <div className="flex flex-col gap-4">
      <div className="p-4 bg-indigo-50 rounded-lg">
        <p className="text-sm text-indigo-800">
          Selected template:{' '}
          <span className="font-medium">{selectedTemplate?.name}</span>
        </p>
        <p className="text-xs text-indigo-600 mt-1">
          {selectedTemplate?.tasks.length} tasks will be imported
        </p>
      </div>

      <div className="p-3 bg-gray-100 border border-gray-200 rounded-lg">
        <p className="text-sm text-gray-700 font-medium">Schedule-Relative Template</p>
        <p className="text-xs text-gray-500 mt-1">
          Task dates will be calculated relative to the project start date using business
          days only (weekends excluded).
        </p>
      </div>

      {exceedsProjectRange && lastTaskEndDate && projectEndDate && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800 font-medium">
            Some tasks will exceed the project end date
          </p>
          <p className="text-xs text-amber-600 mt-1">
            Tasks would end on{' '}
            <span className="font-medium">{formatForDisplay(lastTaskEndDate)}</span>, but
            the project ends on{' '}
            <span className="font-medium">{formatForDisplay(projectEndDate)}</span>.
          </p>

          <label className="flex items-center gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={adjustEndDates}
              onChange={(e) => setAdjustEndDates(e.target.checked)}
              className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-sm text-amber-800">
              Adjust task end dates to fit within project end date
            </span>
          </label>

          {!adjustEndDates && (
            <p className="text-xs text-amber-600 mt-2 italic">
              If unchecked, you can adjust task dates manually after importing.
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      confirmText={getConfirmText()}
      handleConfirm={handleConfirm}
      size="large"
      isLoading={isLoading}
      disableConfirm={!selectedTemplate}>
      {step === 'configure' && (
        <button type="button"
          onClick={handleBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>
      )}

      {step === 'select' && renderSelectStep()}
      {step === 'configure' && renderConfigureStep()}
    </Modal>
  );
};

export default SelectTemplateModal;
